import type { Clip, CutStyle, CutTransition, Segment } from "./clip.js";

export const TRANSITION_STYLES: readonly CutStyle[] = ["dissolve", "whip", "zoom", "flash", "slide", "blur"];
export const isTransition = (style: CutStyle): boolean => TRANSITION_STYLES.includes(style);

/** A cut's own transition binds to the piece it follows by that piece's source out-point; a cut nudged this far by a trim is still that cut. */
export const CUT_MATCH_MS = 600;
/** The two hand-overs that are not between pieces, keyed below zero: the opener into the video, and the video into the end card. */
export const OPENER_CUT = -1;
export const END_CUT = -2;
export type Boundary = "opener" | "end";
const boundaryKey = (b: Boundary) => (b === "opener" ? OPENER_CUT : END_CUT);

/** How one cut (the one after piece i) plays: its style, its length, and whether it was set on this cut or is the clip's own. */
export interface CutSpec { style: CutStyle; ms: number; own: boolean }

type CutClip = Pick<Clip, "segments" | "cutStyle" | "transitionMs"> & { cutTransitions?: CutTransition[] };

/** Which cut each own transition sits on (index = the piece before it): the nearest cut within CUT_MATCH_MS, one transition per cut. */
export function bindTransitions(segments: Segment[], transitions: CutTransition[]): Map<number, CutTransition> {
  const cands: { i: number; t: CutTransition; d: number }[] = [];
  // the opener's and the end card's hand-overs live below zero: they never bind to a piece
  for (const t of transitions.filter((x) => x.afterMs >= 0)) for (let i = 0; i < segments.length - 1; i++) { const d = Math.abs(segments[i].outMs - t.afterMs); if (d <= CUT_MATCH_MS) cands.push({ i, t, d }); }
  cands.sort((a, b) => a.d - b.d);
  const out = new Map<number, CutTransition>();
  const used = new Set<CutTransition>();
  for (const c of cands) { if (out.has(c.i) || used.has(c.t)) continue; out.set(c.i, c.t); used.add(c.t); }
  return out;
}

/** Every cut of the clip in playback order: cut i is between piece i and piece i + 1. */
export function cutsOf(clip: CutClip): CutSpec[] {
  const bound = bindTransitions(clip.segments, clip.cutTransitions ?? []);
  return clip.segments.slice(0, -1).map((_, i) => { const t = bound.get(i); return t ? { style: t.style, ms: t.ms ?? clip.transitionMs, own: true } : { style: clip.cutStyle, ms: clip.transitionMs, own: false }; });
}

/** The opener's hand-over into the video, or the video's into the end card: its own transition, else the clip's own style. */
export function boundaryCut(clip: CutClip, b: Boundary): CutSpec {
  const key = boundaryKey(b);
  const t = (clip.cutTransitions ?? []).find((x) => x.afterMs === key);
  return t ? { style: t.style, ms: t.ms ?? clip.transitionMs, own: true } : { style: clip.cutStyle, ms: clip.transitionMs, own: false };
}

/** Which hand-over a cut index names: -1 is the opener's, the one after the last piece is the end card's, the rest sit between pieces. */
export const boundaryOf = (clip: Pick<Clip, "segments">, i: number): Boundary | null => (i === -1 ? "opener" : i === clip.segments.length - 1 ? "end" : null);

/** The cut after piece i (-1: after the opener; after the last piece: into the end card); outside the clip it is the clip's own style. */
export function cutAt(clip: CutClip, i: number): CutSpec {
  const b = boundaryOf(clip, i);
  if (b) return boundaryCut(clip, b);
  return cutsOf(clip)[i] ?? { style: clip.cutStyle, ms: clip.transitionMs, own: false };
}

/** The style a piece arrives with: the cut before it, or the clip's own for the first piece (a punch-in lands on every piece that way). */
export const styleInto = (clip: CutClip, pieceIndex: number): CutStyle => (pieceIndex <= 0 ? clip.cutStyle : cutAt(clip, pieceIndex - 1).style);

/** Does any cut play a real transition (an overlap between two pieces)? */
export const anyTransition = (clip: CutClip): boolean => cutsOf(clip).some((c) => isTransition(c.style));

/** The same clip with cut i given its own transition (null puts it back on the clip's own style). */
export function setCutTransition<C extends CutClip>(clip: C, i: number, style: CutStyle | null, ms?: number): C {
  const b = boundaryOf(clip, i);
  if (b) {
    const key = boundaryKey(b);
    const kept = (clip.cutTransitions ?? []).filter((t) => t.afterMs !== key);
    return { ...clip, cutTransitions: style ? [...kept, { afterMs: key, style, ...(ms != null ? { ms } : {}) }] : kept };
  }
  const seg = clip.segments[i];
  if (!seg || i >= clip.segments.length - 1) return clip;
  const bound = bindTransitions(clip.segments, clip.cutTransitions ?? []);
  const old = bound.get(i);
  const kept = (clip.cutTransitions ?? []).filter((t) => t !== old);
  const next = style ? [...kept, { afterMs: seg.outMs, style, ...(ms != null ? { ms } : {}) }] : kept;
  return { ...clip, cutTransitions: next };
}

/** A cut was trimmed (its piece's out-point moved): its transition follows it. */
export function rekeyTransition<C extends CutClip>(clip: C, fromOutMs: number, toOutMs: number): C {
  if (fromOutMs === toOutMs) return clip;
  const list = clip.cutTransitions ?? [];
  if (!list.some((t) => t.afterMs === fromOutMs)) return clip;
  return { ...clip, cutTransitions: list.map((t) => (t.afterMs === fromOutMs ? { ...t, afterMs: toOutMs } : t)) };
}

/** Transitions that no longer sit on any cut (their piece was deleted or merged away): dropped, so the file stays tidy. */
export function pruneTransitions<C extends CutClip>(clip: C): C {
  const list = clip.cutTransitions ?? [];
  if (!list.length) return clip;
  const bound = new Set(bindTransitions(clip.segments, list).values());
  const kept = list.filter((t) => t.afterMs < 0 || bound.has(t)); // the hand-overs at either end always have their place
  return kept.length === list.length ? clip : { ...clip, cutTransitions: kept };
}
